import React, {useContext, useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {ContentEditRoute, ConfigContext, i18nConfigFieldToString} from '@dudagroup/editor'
import {AppContext} from '@dudagroup/editor/lib/client/control/appContext'
import {Pagination} from 'rsuite'
import {useSearchEditorQuery, useSearchEditorSuggestionsQuery} from '../../../api'

const SEARCH_DEBOUNCE_MS = 300
const PAGE_SIZE = 30

function useSearchTranslation() {
  const [{uiLanguage}] = useContext(AppContext)
  const {i18n} = useTranslation()
  // The CMS shell and custom views can use separate i18next instances. Read the menu's
  // language from AppContext so both use the same labels after a language switch.
  return {t: i18n.getFixedT(uiLanguage), language: uiLanguage}
}

// Persist the search across navigation, so returning from a record (browser back, or reopening
// Suche) restores the term, filters and page. Session-scoped, per-viewer; try/caught because
// storage can be unavailable (private windows, blocked site data).
const STATE_KEY = 'snf.globalSearch.v1'
interface PersistedState {
  inputValue: string
  term: string
  searchInBody: boolean
  activeType: string | null
  activeStatus: string | null
  activeModified: string | null
  activeEditor: string | null
  moreOpen: boolean
  page: number
}
function loadState(): Partial<PersistedState> {
  try {
    return JSON.parse(sessionStorage.getItem(STATE_KEY) || '{}')
  } catch {
    return {}
  }
}
function saveState(s: PersistedState) {
  try {
    sessionStorage.setItem(STATE_KEY, JSON.stringify(s))
  } catch {
    /* storage unavailable — non-fatal */
  }
}

// Snippets come from record content, so escape before injecting; the only markup we add is the
// <mark> highlight around the **…** the backend wrapped matched segments in.
function toHighlightedHtml(snippet: string): string {
  return escapeHtml(snippet).replace(/\*\*(.+?)\*\*/g, '<mark>$1</mark>')
}
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

const STYLES = `
/* Explicit width (not just max-width) so the column stays one size regardless of how much
   content is below — otherwise the editor's flex content area shrink-wraps it to the widest
   row, making the search box jump between widths as results change. */
.gsearch { width: 1200px; max-width: 100%; margin: 0 auto; padding: 22px 24px 72px;
  font-size: 14px; color: #1b1e24; box-sizing: border-box; }
.gsearch__panel { width: 100%; }

/* One calm controls card holds search + facets + count/pager. Not sticky — it sits in normal
   flow so nothing scrolls behind it. The pager also repeats at the bottom of the list. */
.gsearch__panel { background: #fff; border: 1px solid #e4e7ee; border-radius: 12px;
  box-shadow: 0 1px 3px rgba(20,28,45,.05); margin-bottom: 20px; }

.gsearch__boxwrap { position: relative; }
.gsearch__box { display: flex; align-items: center; gap: 11px; height: 54px; padding: 0 16px;
  border-bottom: 1px solid #eef1f5; }

/* autocomplete dropdown, overlaying whatever is below the input */
.gsearch__suggest { position: absolute; top: 100%; left: 0; right: 0; z-index: 20; background: #fff;
  border: 1px solid #d3d9e3; border-top: 0; border-radius: 0 0 12px 12px;
  box-shadow: 0 10px 24px rgba(20,28,45,.14); overflow: hidden; }
.gsearch__sitem { display: flex; align-items: center; gap: 9px; width: 100%; text-align: left;
  border: 0; background: transparent; font: inherit; font-size: 14px; color: #3a4150;
  padding: 9px 16px; cursor: pointer; }
.gsearch__sitem svg { width: 14px; height: 14px; color: #b6bcc8; flex: none; }
.gsearch__sitem:hover, .gsearch__sitem.is-active { background: #f0f4fb; }
.gsearch__sitem b { font-weight: 600; color: #1b1e24; }
.gsearch__box svg { width: 19px; height: 19px; color: #9aa2b1; flex: none; }
.gsearch__box input { flex: 1 1 auto; min-width: 0; border: 0; outline: 0; background: transparent;
  font: inherit; font-size: 17px; color: #1b1e24; }
.gsearch__box input::placeholder { color: #aeb4c0; }
.gsearch__clear { border: 0; background: transparent; color: #aeb4c0; cursor: pointer; font-size: 20px;
  line-height: 1; padding: 4px 6px; flex: none; border-radius: 6px; }
.gsearch__clear:hover { color: #5a6272; background: #f2f4f8; }

.gsearch__controls { padding: 12px 16px 4px; }
.gsearch__body-toggle { display: inline-flex; align-items: center; gap: 7px; font-size: 13px;
  color: #5a6272; cursor: pointer; user-select: none; }

.gsearch__facets { display: flex; flex-direction: column; gap: 8px; margin-top: 12px; }
.gsearch__frow { display: flex; gap: 8px; align-items: flex-start; }
.gsearch__flabel { flex: none; width: 74px; font-size: 10px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .06em; color: #a2a9b8; padding-top: 6px; }
.gsearch__chips { display: flex; gap: 6px; flex-wrap: wrap; flex: 1; min-width: 0; }
.gsearch__chip { font: inherit; font-size: 12.5px; color: #4a5262; background: #f4f6fa;
  border: 1px solid transparent; border-radius: 999px; padding: 4px 11px; cursor: pointer;
  max-width: 100%; overflow-wrap: anywhere;
  display: inline-flex; align-items: baseline; gap: 6px; transition: background .12s, color .12s; }
.gsearch__chip:hover { background: #e9edf4; }
.gsearch__chip .n { font-size: 11px; color: #9aa2b1; font-variant-numeric: tabular-nums; }
.gsearch__chip.is-active { background: #2f56c8; color: #fff; }
.gsearch__chip.is-active .n { color: rgba(255,255,255,.8); }
/* A facet value the current filters leave nothing of. Kept in place rather than removed so the
   panel does not reflow on every click — the counts now move with every other facet. */
.gsearch__chip.is-empty, .gsearch__chip.is-empty:hover { background: #f8f9fc; color: #c3c8d3;
  cursor: default; }
.gsearch__chip.is-empty .n { color: #ced3dc; }
.gsearch__more { background: transparent; color: #2f56c8; }
.gsearch__more:hover { background: #e8edfb; }

/* "Weitere Filter" disclosure: date and editor facets stay folded away to keep the panel
   short. Align the disclosure with the facet values. */
.gsearch__disclosure { display: flex; align-items: center; gap: 6px; margin-left: 82px;
  border: 0; background: transparent; font: inherit; font-size: 12.5px; color: #5a6272;
  cursor: pointer; padding: 2px 0; }
.gsearch__disclosure:hover { color: #2f56c8; }
.gsearch__disclosure svg { width: 12px; height: 12px; transition: transform .12s; }
.gsearch__disclosure.is-open svg { transform: rotate(90deg); }
.gsearch__disclosure .n { font-size: 11px; color: #fff; background: #2f56c8; border-radius: 999px;
  padding: 0 6px; font-variant-numeric: tabular-nums; }
@media (prefers-reduced-motion: reduce) { .gsearch__disclosure svg { transition: none; } }

.gsearch__bar { display: flex; align-items: center; gap: 14px; padding: 12px 16px;
  border-top: 1px solid #eef1f5; margin-top: 8px; min-height: 34px; background: #fafbfd;
  border-radius: 0 0 12px 12px; }
.gsearch__count { font-size: 13px; color: #6a7180; font-variant-numeric: tabular-nums; }
.gsearch__count b { color: #1b1e24; font-weight: 600; }
.gsearch__clearall { border: 0; background: transparent; color: #2f56c8; cursor: pointer; font: inherit;
  font-size: 13px; padding: 0; }
.gsearch__clearall:hover { text-decoration: underline; }
.gsearch__bar .rs-pagination-group { margin-left: auto; }
.gsearch__spin { width: 14px; height: 14px; border: 2px solid #c7cede; border-top-color: #2f56c8;
  border-radius: 50%; animation: gsearch-spin .7s linear infinite; flex: none; }
@keyframes gsearch-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .gsearch__spin { animation: none; } }

/* Keep four shared columns even on narrow screens; scroll the table instead of squeezing its text. */
.gsearch__results { overflow-x: auto; }
/* The CMS rich-text tableMenu styles target every table and td, so reset their margin, whitespace and borders here. */
.gsearch__table { width: 100%; min-width: 700px; margin: 0; white-space: normal;
  table-layout: fixed; border-collapse: collapse; }
.gsearch__type-column { width: 16%; }
.gsearch__status-column { width: 112px; }
.gsearch__modified-column { width: 175px; }
.gsearch__table th { padding: 0 12px 8px; text-align: left; font-size: 12px;
  font-weight: 400; color: #6b7280; }
.gsearch__table td { padding: 12px; vertical-align: top; border: 0; border-top: 1px solid #edf0f4;
  border-bottom: 1px solid #edf0f4; overflow-wrap: anywhere; }
/* WebKit needs a transformed table row to contain the stretched link within that row. */
.gsearch__row { position: relative; transform: translate(0); }
.gsearch__row:hover, .gsearch__row:focus-within { background: #f5f8fd; }
.gsearch__type { font-size: 12px; color: #6b7280; }
.gsearch__content { display: block; color: inherit; text-decoration: none; }
.gsearch__content:hover { color: inherit; text-decoration: none; }
/* Stretch the native link over its row so every cell shares its click, context-menu and keyboard behavior. */
.gsearch__content::after { content: ''; position: absolute; inset: 0; }
.gsearch__content:focus-visible { outline: none; }
.gsearch__content:focus-visible::after { outline: 2px solid #2f56c8; outline-offset: -2px; }
.gsearch__rtitle { font-weight: 600; font-size: 14.5px; color: #1b1e24; }
.gsearch__status { display: inline-block; font-size: 11px; font-weight: 500; padding: 2px 9px;
  border-radius: 999px; }
.gsearch__status--Published { color: #1f7a4d; background: #e4f3ea; }
.gsearch__status--Draft { color: #2f56c8; background: #e8edfb; }
.gsearch__status--Archived { color: #8a6100; background: #f6eccf; }
.gsearch__status--Trash { color: #97431f; background: #f6e0d7; }
/* The search ticket prioritizes the complete lead over equal result-row heights. */
.gsearch__snip { margin-top: 3px; font-size: 13px; color: #737a87;
  white-space: normal; overflow-wrap: anywhere; }
.gsearch__snip mark { background: #ffdf6b; color: #3a2d00; font-weight: 600; border-radius: 2px; padding: 0 2px; }
.gsearch__modified { font-size: 12px; color: #6b7280; font-variant-numeric: tabular-nums; }
@media (max-width: 640px) {
  .gsearch__frow { flex-direction: column; }
  .gsearch__chips { width: 100%; }
}

.gsearch__empty { margin-top: 48px; text-align: center; color: #a2a9b8; font-size: 14px; }
.gsearch__pager-bottom { display: flex; justify-content: center; margin-top: 24px; }
`

interface Bucket {
  value: string
  label: string
  count: number
}

/**
 * Facet counts are drill-down: each dimension is counted against the *other* active filters.
 * A selected value can therefore fall to zero and drop out of its own list — leaving a filter
 * that is narrowing the results with no chip to switch it off. Put it back, at zero.
 */
function withActive(buckets: Bucket[], active: string | null): Bucket[] {
  if (!active || buckets.some((b) => b.value === active)) return buckets
  return [...buckets, {value: active, label: active, count: 0}]
}

function FacetRow(props: {
  label: string
  buckets: Bucket[]
  active: string | null
  maxVisible?: number
  onSelect: (value: string | null) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const {t} = useSearchTranslation()
  if (props.buckets.length === 0) return null

  const cap = props.maxVisible ?? Infinity
  // Always keep the active chip visible even when collapsed.
  const visible =
    expanded || props.buckets.length <= cap
      ? props.buckets
      : props.buckets.filter((b, i) => i < cap || b.value === props.active)
  const hidden = props.buckets.length - visible.length

  return (
    <div className="gsearch__frow">
      <span className="gsearch__flabel">{props.label}</span>
      <div className="gsearch__chips">
        {visible.map((b) => {
          const isActive = props.active === b.value
          // Nothing left to select under the other filters — shown, but inert.
          const isEmpty = b.count === 0 && !isActive
          return (
            <button
              key={b.value}
              type="button"
              className={`gsearch__chip${isActive ? ' is-active' : ''}${
                isEmpty ? ' is-empty' : ''
              }`}
              aria-pressed={isActive}
              disabled={isEmpty}
              onClick={() => props.onSelect(isActive ? null : b.value)}>
              {b.label} <span className="n">{b.count}</span>
            </button>
          )
        })}
        {hidden > 0 && !expanded && (
          <button
            type="button"
            className="gsearch__chip gsearch__more"
            onClick={() => setExpanded(true)}>
            {t('globalSearch.moreFacets', '+{{count}} mehr', {count: hidden})}
          </button>
        )}
        {expanded && props.buckets.length > cap && (
          <button
            type="button"
            className="gsearch__chip gsearch__more"
            onClick={() => setExpanded(false)}>
            {t('globalSearch.lessFacets', 'weniger')}
          </button>
        )}
      </div>
    </div>
  )
}

/** Searches CMS records with menu labels and the latest edit date across readable revisions. */
export function CustomViewGlobalSearch() {
  const {t, language} = useSearchTranslation()
  const configs = useContext(ConfigContext)
  const contentTypeLabel = (type: string) => {
    const model = configs.extensionMerged.find((item) => item.identifier === type)
    return (model && i18nConfigFieldToString(model.namePlural, language)) || type
  }
  const dateFormatter = new Intl.DateTimeFormat(language, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })

  const initial = useRef(loadState()).current
  const [inputValue, setInputValue] = useState(initial.inputValue ?? '')
  const [term, setTerm] = useState(initial.term ?? '')
  const [searchInBody, setSearchInBody] = useState(initial.searchInBody ?? false)
  const [activeType, setActiveType] = useState<string | null>(initial.activeType ?? null)
  const [activeStatus, setActiveStatus] = useState<string | null>(initial.activeStatus ?? null)
  // Second-tier facets, folded behind the "Weitere Filter" disclosure.
  const [activeModified, setActiveModified] = useState<string | null>(
    initial.activeModified ?? null
  )
  const [activeEditor, setActiveEditor] = useState<string | null>(initial.activeEditor ?? null)
  const [moreOpen, setMoreOpen] = useState(
    // Never hide a filter that is already narrowing the results.
    initial.moreOpen ?? !!(initial.activeModified || initial.activeEditor)
  )
  const [page, setPage] = useState(initial.page ?? 0)
  // Autocomplete dropdown state.
  const [suggestOpen, setSuggestOpen] = useState(false)
  const [activeSuggest, setActiveSuggest] = useState(-1)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const blurRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      if (blurRef.current) clearTimeout(blurRef.current)
    }
  }, [])

  useEffect(() => {
    saveState({
      inputValue,
      term,
      searchInBody,
      activeType,
      activeStatus,
      activeModified,
      activeEditor,
      moreOpen,
      page,
    })
  }, [
    inputValue,
    term,
    searchInBody,
    activeType,
    activeStatus,
    activeModified,
    activeEditor,
    moreOpen,
    page,
  ])

  const clearFacets = () => {
    setActiveType(null)
    setActiveStatus(null)
    setActiveModified(null)
    setActiveEditor(null)
    setPage(0)
  }

  const onChange = (value: string) => {
    setInputValue(value)
    setSuggestOpen(true)
    setActiveSuggest(-1)
    clearFacets()
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setTerm(value.trim()), SEARCH_DEBOUNCE_MS)
  }
  const clearInput = () => {
    setInputValue('')
    setTerm('')
    setSuggestOpen(false)
    clearFacets()
  }
  const applySuggestion = (s: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    setInputValue(s)
    setTerm(s)
    setSuggestOpen(false)
    setActiveSuggest(-1)
    clearFacets()
  }

  // Suggestions for the term being typed (query completions), scoped to editor content.
  const {data: suggestData} = useSearchEditorSuggestionsQuery({
    skip: inputValue.trim().length < 2,
    fetchPolicy: 'no-cache',
    variables: {term: inputValue.trim(), language, first: 8},
  })
  const suggestions = (suggestData?.extensions.searchEditorSuggestions.suggestions ?? []).filter(
    (s) => s.toLowerCase() !== inputValue.trim().toLowerCase()
  )
  const showSuggest = suggestOpen && suggestions.length > 0

  const onInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setSuggestOpen(false)
      return
    }
    if (!showSuggest) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveSuggest((i) => Math.min(i + 1, suggestions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveSuggest((i) => Math.max(i - 1, -1))
    } else if (e.key === 'Enter' && activeSuggest >= 0) {
      e.preventDefault()
      applySuggestion(suggestions[activeSuggest])
    }
  }

  // A "quoted" query means exact-phrase search; strip the quotes and pass isPhraseSearch.
  const isPhrase = term.length >= 2 && term.startsWith('"') && term.endsWith('"')
  const effectiveTerm = isPhrase ? term.slice(1, -1).trim() : term

  const {data, loading, error} = useSearchEditorQuery({
    skip: effectiveTerm.length === 0,
    fetchPolicy: 'no-cache',
    variables: {
      term: effectiveTerm,
      searchInBody,
      language,
      isPhraseSearch: isPhrase,
      first: PAGE_SIZE,
      skip: page,
      types: activeType ? [activeType] : undefined,
      status: activeStatus ?? undefined,
      modifiedSince: activeModified ?? undefined,
      editor: activeEditor ?? undefined,
    },
  })

  const result = data?.extensions.searchEditor
  const hits = result?.hits ?? []

  // The query runs `no-cache`, so `data` goes undefined on every refetch — a facet click would
  // empty all the chip rows and refill them a moment later. Hold the last counts for the same
  // term/scope so only the numbers change, not the layout. A new term clears them.
  const lastFacets = useRef<{key: string; value: NonNullable<typeof result>} | null>(null)
  const facetKey = `${effectiveTerm}|${searchInBody}`
  if (result) lastFacets.current = {key: facetKey, value: result}
  const facets =
    result ?? (lastFacets.current?.key === facetKey ? lastFacets.current.value : undefined)

  const typeBuckets = withActive(
    (facets?.typeCounts ?? []).map((tc) => ({
      value: tc.contentType,
      label: tc.contentType,
      count: tc.count,
    })),
    activeType
  ).map((bucket) => ({...bucket, label: contentTypeLabel(bucket.value)}))
  const statusBuckets = withActive(
    (facets?.statusCounts ?? []).map((s) => ({
      value: s.value,
      label: t(`globalSearch.status.${s.value}`, s.value),
      count: s.count,
    })),
    activeStatus
  )
  const dateBuckets = withActive(
    (facets?.dateCounts ?? []).map((d) => ({
      value: d.value,
      label: t(`globalSearch.date.${d.value}`, d.value),
      count: d.count,
    })),
    activeModified
  )
  const editorBuckets = withActive(
    (facets?.editorCounts ?? []).map((e) => ({
      value: e.id,
      label: e.name,
      count: e.count,
    })),
    activeEditor
  )
  const hasFacets = typeBuckets.length + statusBuckets.length > 0
  const hasMoreFacets = dateBuckets.length + editorBuckets.length > 0
  const moreFilterCount = (activeModified ? 1 : 0) + (activeEditor ? 1 : 0)
  const anyFilter = !!(activeType || activeStatus || activeModified || activeEditor)
  const hasTerm = effectiveTerm.length > 0
  // With nothing found and nothing filtered, every chip would read 0 — that whole block is
  // noise. Once a filter is on it has to stay, or there is no way back to a non-empty result.
  const showFacets = (hasFacets || hasMoreFacets) && ((facets?.totalCount ?? 0) > 0 || anyFilter)

  const pager = result && result.totalCount > PAGE_SIZE && (
    <Pagination
      prev
      next
      size="sm"
      maxButtons={7}
      ellipsis
      boundaryLinks
      total={result.totalCount}
      limit={PAGE_SIZE}
      activePage={page + 1}
      onChangePage={(p) => setPage(p - 1)}
    />
  )

  return (
    <div className="gsearch">
      <style>{STYLES}</style>

      <div className="gsearch__panel">
        <div className="gsearch__boxwrap">
          <div className="gsearch__box">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round">
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              value={inputValue}
              onChange={(e) => onChange(e.target.value)}
              onFocus={() => setSuggestOpen(true)}
              onBlur={() => {
                // Delay so a click on a suggestion lands before the dropdown closes.
                blurRef.current = setTimeout(() => setSuggestOpen(false), 120)
              }}
              onKeyDown={onInputKeyDown}
              placeholder={t('globalSearch.placeholder', 'Alle Inhalte durchsuchen …')}
              aria-label={t('globalSearch.placeholder', 'Alle Inhalte durchsuchen …')}
              spellCheck={false}
              autoFocus
            />
            {inputValue && (
              <button
                type="button"
                className="gsearch__clear"
                aria-label={t('globalSearch.clear', 'Leeren')}
                onClick={clearInput}>
                ×
              </button>
            )}
          </div>

          {showSuggest && (
            <div className="gsearch__suggest">
              {suggestions.map((s, i) => (
                <button
                  key={s}
                  type="button"
                  className={`gsearch__sitem${i === activeSuggest ? ' is-active' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => applySuggestion(s)}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round">
                    <circle cx="11" cy="11" r="7" />
                    <path d="m21 21-4.3-4.3" />
                  </svg>
                  <span>{s}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="gsearch__controls">
          <label className="gsearch__body-toggle">
            <input
              type="checkbox"
              checked={searchInBody}
              onChange={(e) => setSearchInBody(e.target.checked)}
            />
            {t('globalSearch.searchInBody', 'Auch im Inhalt suchen')}
          </label>

          {hasTerm && showFacets && (
            <div className="gsearch__facets">
              <FacetRow
                label={t('globalSearch.facetType', 'Typ')}
                buckets={typeBuckets}
                active={activeType}
                maxVisible={8}
                onSelect={(v) => {
                  setActiveType(v)
                  setPage(0)
                }}
              />
              <FacetRow
                label={t('globalSearch.facetStatus', 'Status')}
                buckets={statusBuckets}
                active={activeStatus}
                onSelect={(v) => {
                  setActiveStatus(v)
                  setPage(0)
                }}
              />
              {hasMoreFacets && (
                <button
                  type="button"
                  className={`gsearch__disclosure${moreOpen ? ' is-open' : ''}`}
                  aria-expanded={moreOpen}
                  onClick={() => setMoreOpen((open) => !open)}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <path d="m9 6 6 6-6 6" />
                  </svg>
                  {t('globalSearch.moreFilters', 'Weitere Filter')}
                  {!moreOpen && moreFilterCount > 0 && <span className="n">{moreFilterCount}</span>}
                </button>
              )}

              {moreOpen && (
                <>
                  <FacetRow
                    label={t('globalSearch.facetModified', 'Bearbeitet')}
                    buckets={dateBuckets}
                    active={activeModified}
                    onSelect={(v) => {
                      setActiveModified(v)
                      setPage(0)
                    }}
                  />
                  <FacetRow
                    label={t('globalSearch.facetEditor', 'Bearbeiter')}
                    buckets={editorBuckets}
                    active={activeEditor}
                    maxVisible={6}
                    onSelect={(v) => {
                      setActiveEditor(v)
                      setPage(0)
                    }}
                  />
                </>
              )}
            </div>
          )}
        </div>

        {hasTerm && (
          <div className="gsearch__bar">
            {loading && <span className="gsearch__spin" aria-hidden="true" />}
            <span className="gsearch__count">
              {loading ? (
                t('globalSearch.loading', 'Suche läuft …')
              ) : result ? (
                <>
                  <b>{result.totalCount}</b> {t('globalSearch.hits', 'Treffer')}
                </>
              ) : null}
            </span>
            {anyFilter && (
              <button type="button" className="gsearch__clearall" onClick={clearFacets}>
                {t('globalSearch.clearFilters', 'Filter zurücksetzen')}
              </button>
            )}
            {pager}
          </div>
        )}
      </div>

      {error && (
        <div className="gsearch__empty">
          {t('globalSearch.error', 'Die Suche ist fehlgeschlagen.')} {error.message}
        </div>
      )}

      {!hasTerm && (
        <div className="gsearch__empty">
          {t('globalSearch.prompt', 'Gib einen Suchbegriff ein, um alle Inhalte zu durchsuchen.')}
        </div>
      )}

      {hasTerm && !loading && hits.length === 0 && !error && (
        <div className="gsearch__empty">
          {t('globalSearch.empty', 'Nichts gefunden für «{{term}}».', {term: effectiveTerm})}
        </div>
      )}

      {hits.length > 0 && (
        <div
          className="gsearch__results"
          role="region"
          aria-label={t('globalSearch.results', 'Resultate')}
          tabIndex={0}>
          <table className="gsearch__table">
            <colgroup>
              <col className="gsearch__type-column" />
              <col />
              <col className="gsearch__status-column" />
              <col className="gsearch__modified-column" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col">{t('globalSearch.facetType', 'Typ')}</th>
                <th scope="col">{t('globalSearch.titleText', 'Titel / Text')}</th>
                <th scope="col">{t('globalSearch.facetStatus', 'Status')}</th>
                <th scope="col">{t('globalSearch.lastEdited', 'Zuletzt editiert am')}</th>
              </tr>
            </thead>
            <tbody>
              {hits.map((hit) => (
                <tr key={`${hit.contentType}-${hit.id}-${hit.revision}`} className="gsearch__row">
                  <td className="gsearch__type">{contentTypeLabel(hit.contentType)}</td>
                  <td>
                    {/* The CMS Link intercepts modified clicks. A native link preserves opening a row in another tab. */}
                    <a
                      className="gsearch__content"
                      href={
                        ContentEditRoute.create({
                          type: hit.contentType,
                          id: hit.id,
                          revision: String(hit.revision),
                        }).path
                      }>
                      <div className="gsearch__rtitle">
                        {hit.title || t('globalSearch.untitled', '(ohne Titel)')}
                      </div>
                      {hit.highlights.length > 0 && (
                        <div
                          className="gsearch__snip"
                          dangerouslySetInnerHTML={{__html: toHighlightedHtml(hit.highlights[0])}}
                        />
                      )}
                    </a>
                  </td>
                  <td>
                    <span className={`gsearch__status gsearch__status--${hit.publicationState}`}>
                      {t(`globalSearch.status.${hit.publicationState}`, hit.publicationState)}
                    </span>
                  </td>
                  <td className="gsearch__modified">
                    {hit.modifiedAt && !Number.isNaN(Date.parse(hit.modifiedAt)) ? (
                      <time dateTime={hit.modifiedAt}>
                        {dateFormatter.format(new Date(hit.modifiedAt))}
                      </time>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pager && <div className="gsearch__pager-bottom">{pager}</div>}
    </div>
  )
}
